import React, { useEffect, useState } from "react";
import axios from "axios";
import ReceiptDownload from "./ReceiptDownload";
import "../styles/FeeHistory.css";
import API_URL from "../apiConfig";

const YEAR_LABELS = [
  "1st Year GNM",
  "2nd Year GNM",
  "3rd Year GNM",
];

function getYearLabel(course_year) {
  return YEAR_LABELS[(Number(course_year) || 1) - 1] || `Year ${course_year}`;
}

function formatCurrency(num) {
  return "₹" + Number(num || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Group fee records yearwise (per class)
function groupByYear(records) {
  const groups = {};
  for (const rec of records) {
    const year = rec.course_year;
    if (!groups[year]) groups[year] = [];
    groups[year].push(rec);
  }
  return groups;
}

function FeeHistory({ user, token }) {
  const [feeRecords, setFeeRecords] = useState([]);
  const [error, setError] = useState("");
  const [selectedYear, setSelectedYear] = useState(""); // for filtering
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFees = async () => {
      setLoading(true);
      setError("");
      try {
        const studentId = user.student_id || user.id || user._id;
        // Use updated API endpoint with /api
        const res = await axios.get(
          `${API_URL}/api/fees/records/${studentId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setFeeRecords(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setError("Could not fetch fee history.");
        setFeeRecords([]);
      }
      setLoading(false);
    };
    fetchFees();
  }, [user, token]);

  // Build yearwise group and available years
  const yearGroups = groupByYear(feeRecords);
  const years = Object.keys(yearGroups).sort((a, b) => Number(a) - Number(b));

  return (
    <div className="feehistory-container">
      <h3 className="feehistory-title">Fee History</h3>
      {error && <div className="feehistory-error">{error}</div>}

      {loading ? (
        <div className="feehistory-loading">Loading...</div>
      ) : (
      <>
      {/* Year selector */}
      {years.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <label>
            <b>Filter by Year/Class: </b>
            <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
              <option value="">All</option>
              {years.map(year => (
                <option value={year} key={year}>{getYearLabel(year)}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {years.length === 0 ? (
        <p>No fee records found.</p>
      ) : (
        (selectedYear ? [selectedYear] : years).map(year => (
          <div key={year}>
            {years.length > 1 && (
              <h4 style={{margin:"8px 0 0 2px"}}>{getYearLabel(year)}</h4>
            )}
            <table className="feehistory-table">
              <thead>
                <tr>
                  <th>Session Year</th>
                  <th>Paid Amount</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Reference</th>
                  <th>Payment Date</th>
                  <th>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {yearGroups[year].map((fee, idx) => {
                  // Status: If paid/amount_paid>0 then Successful, else Unpaid
                  let status = "Unpaid";
                  if ((fee.status && fee.status.toLowerCase() === "paid") || (Number(fee.amount_paid) > 0)) {
                    status = "Successful";
                  }
                  return (
                    <tr key={fee.fee_id || fee.id || idx}>
                      <td>{fee.session_year}</td>
                      <td>{formatCurrency(fee.amount_paid || fee.amount)}</td>
                      <td className={status === "Successful" ? "paid" : "due"}>{status}</td>
                      <td>{fee.mode || "-"}</td>
                      <td>{fee.reference || "-"}</td>
                      <td>{fee.payment_date ? new Date(fee.payment_date).toLocaleDateString() : "-"}</td>
                      <td>
                        {Number(fee.amount_paid || fee.amount) > 0 ? (
                          <button
                            onClick={() => ReceiptDownload.generatePDF({
                              ...fee,
                              student_name: user.name || user.student_name || "Student",
                              course_year: getYearLabel(fee.course_year),
                              roll_no: user.roll_no || fee.roll_no,
                              session_year: fee.session_year
                            })}
                            className="receipt-download-btn"
                          >
                            Download
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
      <div className="feehistory-note">
        <b>Note:</b> Contact the office for any payment discrepancies.
      </div>
      </>
      )}
    </div>
  );
}

export default FeeHistory;